import { T, type Trilingual } from './i18n.ts';
import type { InboxDelivery, InboxItem } from './api.ts';

/**
 * 收件箱页面的文案。
 *
 * ════════════════════════════════════════════════════════
 * 为什么文案在界面层，而判定在服务端
 * ════════════════════════════════════════════════════════
 * 服务端那一层（`@jyfk/notify` 的 `outbox-view.ts`）只给**码**：
 * `state` 是什么状况、`cause` 是谁的事。一个中文字都不产出。
 *
 * 因为界面要三语。判定写在服务端、文案写在这里，两边各只有一份：
 *   判定只有一份  → 同一种失败不会在列表上说一句、在详情里说另一句
 *   文案只有一份  → 不会漏翻某一种语言
 * 反过来（服务端直接给中文）的表现是：香港和英文用户看到一段简体，
 * 而**没有任何一处会报错**。
 *
 * ── 这一页的文案有一条特别的规矩 ──────────────────────
 * **三种「没收到」要说成三种话**（`docs/现状与欠账.md` 4.9 节的教训）：
 *   我们这边没做   → 说实话，别让他去查自己的配置
 *   他那边没配齐   → 给他能照着做的一句话 + 去哪里填
 *   上游临时失败   → 说「还在重试，下次几点」
 * 揉成一句「发送失败」等于把排查成本原封不动推给用户。
 */

/* ══════════════════════ 页面骨架 ══════════════════════ */

/** 收件箱里处理了一条（确认 / 延后 / 已处理）之后发这个事件，顶栏铃铛马上重新数 */
export const INBOX_CHANGED = 'jyfk:inbox-changed';

export const IX = {
  kicker: T('为什么没提醒我', '為什麼沒提醒我', 'Did it reach you?'),
  title: T('发出去的每一条', '發出去的每一條', 'Every alert we sent'),
  sub: T('每一条提醒往哪几个通道发了、到了没到、没到是为什么。这一页只记已经发出去的 —— 一个通道都没配的话，提醒根本不会进来。',
    '每一條提醒往哪幾個通道發了、到了沒到、沒到是為什麼。這一頁只記已經發出去的 —— 一個通道都沒配的話，提醒根本不會進來。',
    'Where each alert was sent, whether it arrived, and why it did not. This page only lists alerts that were actually queued: with no channel set up, nothing is ever sent.'),
  refresh: T('刷新', '重新載入', 'Refresh'),
  loading: T('正在读…', '正在讀…', 'Loading…'),
  readFail: T('读不到收件箱。', '讀不到收件匣。', 'Could not load your inbox.'),

  /* ── 左栏：筛选 ── */
  filter: T('筛选', '篩選', 'Filter'),
  fAll: T('全部', '全部', 'All'),
  fFailed: T('没送到', '沒送到', 'Not delivered'),
  fPending: T('还在路上', '還在路上', 'In flight'),
  fSent: T('已送达', '已送達', 'Delivered'),
  fDropped: T('放弃了', '放棄了', 'Given up'),
  countsNote: T('这几个数按「每个通道一条」算；右边的列表按「一条提醒」算，所以两边对不上是正常的。',
    '這幾個數按「每個通道一條」算；右邊的列表按「一條提醒」算，所以兩邊對不上是正常的。',
    'These counts are per channel. The list on the right is per alert, so the two numbers differ on purpose.'),
  channel: T('按通道', '按通道', 'By channel'),

  /* ── 中栏：列表 ── */
  list: T('提醒流水', '提醒流水', 'Alert stream'),
  empty: T('还没有发出去过任何提醒。', '還沒有發出去過任何提醒。', 'No alerts have been sent yet.'),
  emptyWhy: T('判定在跑、而且有事件触发的时候，这里才会有记录。空着说明这段时间什么都没触发 —— 那是好事。',
    '判定在跑、而且有事件觸發的時候，這裏才會有記錄。空著說明這段時間什麼都沒觸發 —— 那是好事。',
    'Records appear here when a rule actually fires. Empty means nothing tripped, which is good news.'),
  emptyFiltered: T('这个筛选下没有记录。', '這個篩選下沒有記錄。', 'Nothing matches this filter.'),
  more: T('再读一些更早的', '再讀一些更早的', 'Load older'),
  noMore: T('没有更早的了。', '沒有更早的了。', 'That is all we have.'),
  keep: T('提醒记录保留 {n} 天（按你的档位），更早的会自动清掉。', '提醒記錄保留 {n} 天（按你的檔位），更早的會自動清掉。',
    'Alert records are kept for {n} days (based on your plan); older ones are cleared automatically.'),

  /* ── 右栏：详情 ── */
  detail: T('这一条的详情', '這一條的詳情', 'Alert detail'),
  pick: T('左边选一条，看它到了没到。', '左邊選一條，看它到了沒到。', 'Pick an alert on the left to see where it went.'),
  channels: T('往这几个通道发了', '往這幾個通道發了', 'Channels'),
  openLink: T('查看详情', '查看詳情', 'Open'),
  sentAt: T('送达于', '送達於', 'Delivered at'),
  tries: T('试了 {n} 次', '試了 {n} 次', 'Attempted {n}×'),
  nextTry: T('下次 {t} 再试', '下次 {t} 再試', 'Next try at {t}'),
  ring: T('接着响的第 {n} 条', '接著響的第 {n} 條', 'Repeat ring #{n}'),
  upstreamSaid: T('上游说：', '上游說：', 'Upstream said: '),
  redacted: T('里面的密钥、会话号、邮箱已经遮掉了。', '裏面的密鑰、會話號、電郵已經遮掉了。',
    'Keys, chat ids and emails are masked.'),

  /* ── 顶部：判定在跑没 ── */
  guardTitle: T('先看这个：判定在跑吗', '先看這個：判定在跑嗎', 'First: is the engine running?'),
  guardNote: T('提醒的前提是判定在跑。它停了的话，下面这张流水会从那一刻起什么都不增加 —— 而那不是「今天很太平」。',
    '提醒的前提是判定在跑。它停了的話，下面這張流水會從那一刻起什麼都不增加 —— 而那不是「今天很太平」。',
    'Alerts only exist if the engine is running. If it stopped, the stream below simply stops growing, which is not the same as a quiet day.'),
  laneJudge: T('纪律判定', '紀律判定', 'Discipline engine'),
  lanePrice: T('价格轮询', '價格輪詢', 'Price polling'),
  laneRemind: T('事项提醒', '事項提醒', 'Reminders'),
  lanePay: T('链上收款', '鏈上收款', 'On-chain payments'),
  laneOn: T('在跑', '在跑', 'Running'),
  laneOff: T('停了', '停了', 'Stopped'),
  laneNever: T('还没启动过', '還沒啟動過', 'Never started'),
  payClosed: T('还没开放', '還沒開放', 'Not open yet'),

  /* ── 一个通道都没配 ── */
  noChannel: T('你一个能叫到你的通道都没配：提醒不会发出，这一页也不会有记录。',
    '你一個能叫到你的通道都沒配：提醒不會發出，這一頁也不會有記錄。',
    'You have no working channel: nothing will be sent, and nothing will show up here.'),
  noChannelHow: T('去「事项提醒」页把 Telegram、飞书或 Bark 里的任意一个配好。iPhone 用 Bark 的话，催办能穿过静音。',
    '去「事項提醒」頁把 Telegram、飛書或 Bark 裏的任意一個配好。iPhone 用 Bark 的話，催辦能穿過靜音。',
    'Set up Telegram, Feishu or Bark on the Reminders page. On iPhone, Bark can break through silent mode.'),
  goSetup: T('去配通道', '去配通道', 'Set up a channel'),

  /* ── 纪律现在要你做什么（指路，不重做） ── */
  nowTitle: T('纪律现在要你做什么', '紀律現在要你做什麼', 'What discipline wants from you now'),
  nowNote: T('熔断、待确认的提现、挂着的催办都在总览页顶上，而且那里才能真的按下去。这一页不重做一遍 —— 两处各一份的话，按了一处另一处还亮着。',
    '熔斷、待確認的提現、掛著的催辦都在總覽頁頂上，而且那裏才能真的按下去。這一頁不重做一遍 —— 兩處各一份的話，按了一處另一處還亮著。',
    'Breakers, pending withdrawals and open nags live at the top of the Overview page, where the buttons actually work. We deliberately do not duplicate them here.'),
  goOverview: T('去总览处理', '去總覽處理', 'Go to Overview'),

  /* ── 游客 ── */
  sample: T('登录后这一页显示的是你自己的：每一条提醒发到了哪几个通道、到了没到、没到是为什么。下面这几条是示例。',
    '登入後這一頁顯示的是你自己的：每一條提醒發到了哪幾個通道、到了沒到、沒到是為什麼。下面這幾條是示例。',
    'Once you sign in, this page shows your own alerts: which channels each one went to, whether it arrived, and why not. The rows below are samples.'),

  /* ── 处理（确认 / 延后 / 已处理）和快捷键（2026-10-29） ── */
  ack: T('确认', '確認', 'Acknowledge'),
  snooze: T('延后 {n} 分钟', '延後 {n} 分鐘', 'Snooze {n} min'),
  done: T('已处理', '已處理', 'Resolve'),
  stAck: T('已确认', '已確認', 'Acknowledged'),
  stDone: T('已处理', '已處理', 'Resolved'),
  stSnoozed: T('延后到 {t}', '延後到 {t}', 'Snoozed until {t}'),
  unread: T('未处理', '未處理', 'Open'),
  triageNote: T('这三个按钮改的是收件箱里这一条算不算「未处理」（顶栏铃铛上的数）。事项提醒上按「已处理」「延后」会同时处理那件事项、停掉后面的催办；纪律那几件事（确认提现、解除熔断）还是要去总览按。',
    '這三個按鈕改的是收件箱裏這一條算不算「未處理」（頂欄鈴鐺上的數）。事項提醒上按「已處理」「延後」會同時處理那件事項、停掉後面的催辦；紀律那幾件事（確認提現、解除熔斷）還是要去總覽按。',
    'These buttons decide whether this alert still counts as open (the number on the top-bar bell). On a reminder, Resolve and Snooze also handle the reminder itself and stop further nagging; discipline actions (confirming a withdrawal, lifting a breaker) still happen on the Overview page.'),
  triaged: T('存好了', '存好了', 'Saved'),
  reminderToo: T('那件事项也一起处理了', '那件事項也一起處理了', 'The reminder itself was handled too'),
  reminderNotDue: T('那件事项还没到点，只在收件箱里延后了', '那件事項還沒到點，只在收件箱裏延後了', 'The reminder is not due yet, so it was only snoozed here'),
  keys: T('快捷键：{next} 下一条 · {prev} 上一条 · {ack} 确认 · {snooze} 延后 · {done} 已处理（在通用设置里改）',
    '快捷鍵：{next} 下一條 · {prev} 上一條 · {ack} 確認 · {snooze} 延後 · {done} 已處理（在通用設定裏改）',
    'Shortcuts: {next} next · {prev} previous · {ack} acknowledge · {snooze} snooze · {done} resolve (change them in General settings)'),
  bell: T('收件箱 · {n} 条未处理', '收件匣 · {n} 條未處理', 'Inbox · {n} open'),
  bellNone: T('收件箱 · 没有未处理的', '收件匣 · 沒有未處理的', 'Inbox · nothing open')
} as const;

/* ══════════════════════ 按码取文案 ══════════════════════ */

/** 一条提醒的结论。四种都要有说法 —— 「没送到」是整页最要紧的那一种 */
export const VERDICT_TEXT: Readonly<Record<InboxItem['verdict'], Trilingual>> = {
  delivered: T('送到了', '送到了', 'Delivered'),
  partial: T('送到了（部分通道没成功）', '送到了（部分通道沒成功）', 'Delivered (some channels failed)'),
  inflight: T('还在路上', '還在路上', 'In flight'),
  none: T('没送到', '沒送到', 'Not delivered')
};

/** 结论对应的标签样式。`.tag` 的那几个修饰类，见 styles/ui.css */
export const VERDICT_TAG: Readonly<Record<InboxItem['verdict'], string>> = {
  delivered: 'tag up',
  partial: 'tag warn',
  inflight: 'tag info',
  none: 'tag urgent'
};

export const STATE_TEXT: Readonly<Record<InboxDelivery['state'], Trilingual>> = {
  sent: T('已送达', '已送達', 'Delivered'),
  queued: T('排队中', '排隊中', 'Queued'),
  sending: T('正在发送', '正在發送', 'Sending'),
  retrying: T('还会再试', '還會再試', 'Retrying'),
  failed: T('送不出去', '送不出去', 'Failed'),
  canceled: T('你取消了', '你取消了', 'Canceled by you'),
  dropped: T('没试就放弃了', '沒試就放棄了', 'Given up without trying')
};

export const STATE_TAG: Readonly<Record<InboxDelivery['state'], string>> = {
  sent: 'tag up',
  queued: 'tag plain',
  sending: 'tag info',
  retrying: 'tag warn',
  failed: 'tag urgent',
  canceled: 'tag plain',
  dropped: 'tag urgent'
};

/**
 * 没送到，该怎么跟他说。
 *
 * ════════════════════════════════════════════════════════
 * 这四句是这一页的核心，每一句都在回答「那我该做什么」
 * ════════════════════════════════════════════════════════
 * `ours` 那一句尤其要小心：那是我们这边没做完（比如平台机器人没配），
 * 而早先的文案听起来像他该去配什么（`docs/现状与欠账.md` 4.9 节）。
 * 说实话的代价是承认我们还没做完，收益是他不会白折腾半小时。
 */
export const CAUSE_TEXT: Readonly<Record<'ours' | 'theirs' | 'upstream' | 'canceled' | 'dnd' | 'unknown', Trilingual>> = {
  /* 时态刻意不定：免打扰可能还开着（还没汇总），也可能已经结束（汇总过了） */
  dnd: T('你开着免打扰，这一条没有推送。免打扰结束后，这期间压下的会汇总成一条补发（除非你关了「结束后汇总补发」）。',
    '你開著免打擾，這一條沒有推送。免打擾結束後，這期間壓下的會匯總成一條補發（除非你關了「結束後匯總補發」）。',
    'Do not disturb was on, so this was not pushed. When it ends, everything held during it goes out as one summary (unless you turned the summary off).'),
  ours: T('这是我们这边还没做完 —— 不用你改任何设置。',
    '這是我們這邊還沒做完 —— 不用你改任何設定。',
    'This one is on us, not on your settings.'),
  theirs: T('这个通道还没配齐，按下面那句话去填一下就能收到。',
    '這個通道還沒配齊，按下面那句話去填一下就能收到。',
    'This channel is not fully set up yet. Fix what it says below and it will work.'),
  upstream: T('对方那边一时不行（限流或者超时）。',
    '對方那邊一時不行（限流或者超時）。',
    'The provider was temporarily unavailable (rate limit or timeout).'),
  canceled: T('你点了「处理完了」，后面的响铃就取消了。',
    '你按了「處理完了」，後面的響鈴就取消了。',
    'You marked it done, so the follow-up rings were canceled.'),
  unknown: T('这个原因我们也没见过。下面是上游的原话，发给我们就能查。',
    '這個原因我們也沒見過。下面是上游的原話，發給我們就能查。',
    'We have not seen this one before. The raw message below is enough for us to look into it.')
};

/** 三个通道的名字。和 `@jyfk/notify` 的 CHANNEL_LABELS 同一套叫法 */
export const CHANNEL_TEXT: Readonly<Record<string, Trilingual>> = {
  telegram: T('Telegram', 'Telegram', 'Telegram'),
  bark: T('Bark（iOS 推送）', 'Bark（iOS 推送）', 'Bark (iOS push)'),
  feishu: T('飞书', '飛書', 'Feishu'),
  /* 浏览器扩展弹系统通知（2026-10-30） */
  browser: T('浏览器弹窗', '瀏覽器彈窗', 'Desktop pop-up')
};

/** 四档打扰强度。和设置页里那几档是同一套说法 */
export const URGENCY_TEXT: Readonly<Record<string, Trilingual>> = {
  passive: T('安静', '安靜', 'Passive'),
  active: T('普通', '普通', 'Normal'),
  timeSensitive: T('要紧', '要緊', 'Time-sensitive'),
  critical: T('紧急', '緊急', 'Critical')
};

export const URGENCY_TAG: Readonly<Record<string, string>> = {
  passive: 'tag plain',
  active: 'tag info',
  timeSensitive: 'tag warn',
  critical: 'tag urgent'
};

/**
 * 紧急提醒只走通一路时多说的那一句。
 *
 * 为什么要专门说：紧急提醒至少两路是**策略**（`urgentDualChannel`），
 * 理由是任何单一通道都有失手的时候，而「熔断了」这条消息送不到的代价
 * 是他继续在上头状态下交易。只成功一路时显示成绿色的「已送达」
 * 等于把这条策略偷偷降级了。
 */
export const WEAK_CRITICAL = T(
  '这是紧急提醒，但只有一个通道成功了。紧急的那几条本来该走两个通道 —— 再配一个，手机静音或者某一家被墙的时候还有退路。',
  '這是緊急提醒，但只有一個通道成功了。緊急的那幾條本來該走兩個通道 —— 再配一個，手機靜音或者某一家被牆的時候還有退路。',
  'This was a critical alert but only one channel got through. Critical alerts are meant to use two, so there is a fallback when a phone is silenced or a provider is blocked.'
);

/** 「几个通道里几个成功」。**不要只说「送到了」** —— 他有权知道哪一路没成 */
export const SENT_RATIO = T('{n} / {m} 个通道成功', '{n} / {m} 個通道成功', '{n} of {m} channels succeeded');

/** 文案里的 `{x}` 占位替换。只有这一页用，所以不往 i18n.ts 里加 */
export function fill(s: string, vars: Readonly<Record<string, string | number>>): string {
  let out = s;
  for (const [k, v] of Object.entries(vars)) out = out.replaceAll('{' + k + '}', String(v));
  return out;
}

/**
 * 这一条该怎么分到左栏那几个筛选里。
 *
 * **纯函数，而且和服务端的筛选是两回事**：服务端按「这条提醒有没有哪一路是
 * 这个状态」挑出哪几条提醒，这里只决定界面上那几个按钮的高亮。
 * 两处口径不一样会让「筛了没送到却看到一条已送达」看起来像 bug，
 * 所以这里写的是同一套判断：看那几路里有没有一路是对应状态。
 */
export type InboxFilter = 'all' | 'failed' | 'pending' | 'sent' | 'dropped';

/** 筛选 → 接口的 `status` 参数。`all` 不传，`failed` 传 failed */
export const FILTER_STATUS: Readonly<Record<InboxFilter, string | undefined>> = {
  all: undefined,
  failed: 'failed',
  pending: 'pending',
  sent: 'sent',
  dropped: 'dropped'
};

export const FILTER_TEXT: Readonly<Record<InboxFilter, Trilingual>> = {
  all: IX.fAll,
  failed: IX.fFailed,
  pending: IX.fPending,
  sent: IX.fSent,
  dropped: IX.fDropped
};

/**
 * 一条提醒落不落在这个筛选里。
 *
 * ════════════════════════════════════════════════════════
 * 这个函数只有游客模式用，但它必须和服务端**一个口径**
 * ════════════════════════════════════════════════════════
 * 游客模式下一个请求都不发（见 lib/api.ts 的说明），所以筛选得在本地做。
 * 本来可以把那几个按钮在游客模式下禁掉，但**画出来而按不动**
 * 是这个项目最大的那个教训（「能改不等于起作用」），所以宁可在这里实现一遍。
 *
 * 口径照服务端那一份：服务端筛的是「这条提醒**有没有哪一路**是这个状态」
 * （而不是「只显示这几路」），所以这里也是 `some`。
 * 两边口径不一样的表现是「筛了没送到，却看到一条已送达」——
 * 看起来像 bug，而其实是我们自己在界面和接口里写了两套话。
 *
 * `dropped` 把「你自己取消的响铃」也算进去：库里它们是同一个状态
 * （`status = 'dropped'`），服务端筛出来就带着它们。
 */
export function matchesFilter(i: InboxItem, f: InboxFilter, channel: string | null): boolean {
  const ds = channel === null ? i.deliveries : i.deliveries.filter(d => d.channel === channel);
  if (ds.length === 0) return false;
  switch (f) {
    case 'all': return true;
    case 'sent': return ds.some(d => d.state === 'sent');
    case 'failed': return ds.some(d => d.state === 'failed');
    case 'dropped': return ds.some(d => d.state === 'dropped' || d.state === 'canceled');
    case 'pending':
      return ds.some(d => d.state === 'queued' || d.state === 'sending' || d.state === 'retrying');
  }
}
